import { Node } from '@tiptap/core'
import { Node as NodeType } from 'prosemirror-model'

import { GeneralOptions } from '@/types'

import { Column } from './Column'
import ColumnsButton from './components/ColumnsButton.vue'

export enum ColumnLayoutEnum {
  SidebarLeft = 'sidebar-left',
  SidebarRight = 'sidebar-right',
  TwoColumn = 'two-column',
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    columns: {
      setColumns: () => ReturnType
      setLayout: (layout: ColumnLayoutEnum) => ReturnType
      removeColumns: () => ReturnType
    }
  }
}
export interface ColumnsOptions extends GeneralOptions<ColumnsOptions> {
  columnOptions: Record<string, unknown>
  layout: ColumnLayoutEnum
}

export const Columns = Node.create<ColumnsOptions>({
  name: 'columns',
  group: 'columns',
  content: 'column+',
  defining: true,
  isolating: true,
  addOptions() {
    return {
      ...this.parent?.(),
      layout: ColumnLayoutEnum.TwoColumn,
      divider: false,
      spacer: false,
      toolbar: true,
      button: ({ editor }) => ({
        component: ColumnsButton,
        componentProps: {
          editor,
          tooltip: 'Columns',
          icon: 'Columns',
        },
      }),
    }
  },

  addAttributes() {
    return {
      layout: {
        default: ColumnLayoutEnum.TwoColumn,
      },
    }
  },
  addCommands() {
    return {
      setColumns:
        () =>
        ({ commands }) => {
          commands.insertContent(
            `<div data-type="columns"><div data-type="column" data-position="left"><p></p></div><div data-type="column" data-position="right"><p></p></div></div>`
          )
          return true
        },

      setLayout:
        (layout: ColumnLayoutEnum) =>
        ({ commands }) =>
          commands.updateAttributes('columns', { layout }),

      removeColumns:
        () =>
        ({ state, dispatch, tr }) => {
          const { $from } = state.selection

          // Find the columns node
          for (let depth = $from.depth; depth >= 0; depth--) {
            const node = $from.node(depth)
            if (node.type.name === 'columns') {
              const pos = $from.before(depth)
              const endPos = pos + node.nodeSize

              // Collect all content from columns
              const content: NodeType[] = []
              node.descendants(child => {
                if (child.type.name === 'column') {
                  child.forEach(blockNode => {
                    content.push(blockNode)
                  })
                }
              })

              // Replace columns with its content
              if (dispatch) {
                tr.replaceWith(pos, endPos, content)
                dispatch(tr)
              }
              return true
            }
          }
          return false
        },
    }
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', { 'data-type': 'columns', class: `layout-${HTMLAttributes.layout}` }, 0]
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-type="columns"]',
      },
    ]
  },
  addExtensions() {
    return [Column.configure(this.options.columnOptions)]
  },
})

export default Columns
